import { useAsync } from '@tiger/hooks';
import { Alert, Form } from 'antd';
import { $Formutil, withForm } from 'react-antd-formutil';
import { Navigate, useNavigate } from 'react-router-dom';
import ErrorBox from 'components/ErrorBox';
import Loading from 'components/Loading';
import { useConfirmModal } from 'components/Modal';
import Toast from 'components/Toast';
import security from 'stores/security';
import LogoffBottom from '../../components/LogoffBottom';
import { applyIBSubmitClose, checkIBClose, getIBCloseStatus } from '../../request';

interface IBCloseProps {
    $formutil: $Formutil<{
        close_confirm: boolean;
    }>;
}

const IBClose: React.FC<IBCloseProps> = ({ $formutil }) => {
    const [{ data, loading, error }, getData] = useAsync(() => Promise.all([checkIBClose(), getIBCloseStatus()]));
    const navigate = useNavigate();
    const { open } = useConfirmModal();

    const submitClose = async () => {
        try {
            await applyIBSubmitClose();

            security.setCloseStatus('PENDING');

            await Toast.show(__('Submitted successfully'));

            navigate('/profile');
        } catch (error) {
            Toast.show(error);
        }
    };

    const handleSubmit = () => {
        const { $invalid, $getFirstError, $batchState } = $formutil;

        if ($invalid) {
            $batchState({
                $touched: true,
                $dirty: true
            });

            Toast.show($getFirstError());
        } else {
            open({
                content: __(
                    'Are you sure to close your account. You can also choose “Cancel” if you change your mind.'
                ),
                okText: __('Next Step'),
                cancelText: __('Cancel'),
                onOk: submitClose
            });
        }
    };

    const renderMain = () => {
        if (!security.isAccountOpened) {
            return <Alert type="error" message={__('User has not opened an account.')} />;
        }

        if (loading) {
            return <Loading />;
        }

        if (error || !data) {
            return <ErrorBox error={error || __('no data')} onClick={getData} />;
        }

        const [checkCloseData, closeStatus] = data;

        const { status, reason } = checkCloseData;

        if (status === false) {
            return <ErrorBox error={reason} />;
        }

        // 如果用户已经提交过申请，我们这里重定向到 个人中心页面

        // PENDING | REVOKING 表示不能提交关闭帐户申请
        if (closeStatus && ['PENDING', 'REVOKING'].includes(closeStatus)) {
            security.setCloseStatus(closeStatus);

            return <Navigate to="/profile" replace />;
        }

        return (
            <>
                <ul className="statement-list">
                    <li className="statement-item">
                        {__(
                            'This Statement governs the relationship between Client(“You”)and M TradeUP Securities, Inc.(“We”or ”Us”),which is a broker-dealer registered at U.S. Securities and Exchange Commission( “SEC”) and member of the Financial Industry Regulatory Authority (“FINRA”) and the Securities Investor Protection Corporation (“SIPC”) that provides online brokerage services. Your click of the button of “Submit” is deemed as your understanding and acceptation of the Statement to close your account at TradeUP Securities, Inc..'
                        )}
                    </li>
                    <li className="statement-item">
                        {__(
                            '1. Ensure Recent Deposits Have Cleared All recent deposits and wire transfers must have time to clear according to the customer deposit credit and hold periods before your request to close your account can be honored. You should be responsible for the possible losses during the account closure period.'
                        )}
                    </li>
                    <li className="statement-item">
                        {__(
                            '2. Liquidate or Transfer Positions Accounts with positions cannot be closed. You must either liquidate or transfer all positions to another brokerage firm to have your assets transferred out of TradeUP Securities, Inc. Please contact us for transfer instructions. You could view your latest statement to verify that all positions have been liquidated or transferred. Your application for account closure means that you voluntarily give up the assets or debts in your account.'
                        )}
                    </li>
                    <li className="statement-item">
                        {__(
                            "3. Convert Foreign Currencies Closing withdrawals may only be made in your base currency. You must convert any foreign currency you may hold back to your account's base currency. Please be sure to check your statement and consider any dividend or interest accruals."
                        )}
                    </li>
                    <li className="statement-item">
                        {__(
                            `4. Verify Bank Wire Instructions Please sell all your positions and withdraw the balance back to your bank account. If you do not have any saved bank information (instruction) for a Wire Withdrawal, create one.`
                        )}
                    </li>
                    <li
                        className="statement-item"
                        dangerouslySetInnerHTML={{
                            __html: __(
                                '5. Check Statements and Tax Forms To access activity statements and tax forms, log in to APP and visit the page of Me and open the Live Account menu. This information will only be available for a limited time after your account is closed. If you need assistance, please let us know by contacting our Customer Service through email <a href="mailto:support@tradeup.com">support@tradeup.com</a> for future access to statements and tax forms.'
                            )
                        }}
                    />
                    <li className="statement-item">
                        {__(
                            '6. Record keeping Requirements Please keep in mind that regulations require us to preserve certain books and records. We take the security of all collected account information and data very seriously, and we don’t intend to use this data for anything beyond the fulfillment of our regulatory requirements.'
                        )}
                    </li>
                    <li
                        className="statement-item"
                        dangerouslySetInnerHTML={{
                            __html: __(
                                'You can view the status of your request to close your account by receiving email from our Customer Service. If the above criteria are not met, our system will be unable to process your account closure request. If you need assistance, please let us know by contacting our Customer Service through email <a href="mailto:support@tradeup.com">support@tradeup.com</a>. We’d be sad to see you go, but we hope you’ve enjoyed the commission-free investing experience.'
                            )
                        }}
                    />
                </ul>
                <LogoffBottom
                    errorMsg={__('You need to confirm all the information to continue to the next page')}
                    checkBoxLabel={__(
                        'I confirm that I have read, understand and agree to the above Broker Dealer Record-Keeping Requirements.'
                    )}
                    btnDisabled={!$formutil.$params.close_confirm}
                />
            </>
        );
    };

    return (
        <Form className="logoff-close-root ib-close-root" onFinish={handleSubmit}>
            {renderMain()}
        </Form>
    );
};

export default withForm(IBClose);
